import {
	faAlignJustify,
	faChevronDown,
	faIcons,
	faRulerVertical,
} from '@fortawesome/pro-solid-svg-icons';
import React, { useRef, useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { getIconDefinition } from '../../../../components/IconSelector';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import type {
	FieldConfiguration,
	Size,
	StyleConfiguration,
} from '../../../types';
import { IconDropdown } from '../../list-configuration/columns/IconDropdown';

interface StyleConfiguratorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	isModalOpen?: boolean;
}

// Size options
const sizeOptions: Array<{ value: Size; label: string }> = [
	{ value: 'xs', label: 'Extra Small' },
	{ value: 'sm', label: 'Small' },
	{ value: 'md', label: 'Medium' },
	{ value: 'lg', label: 'Large' },
	{ value: 'xl', label: 'Extra Large' },
];

// Variant options (for applicable display styles)
const variantOptions: Array<{
	value: NonNullable<StyleConfiguration['variant']>;
	label: string;
}> = [
	{ value: 'solid', label: 'Solid' },
	{ value: 'outline', label: 'Outline' },
	{ value: 'ghost', label: 'Ghost' },
];

export function StyleConfigurator({
	field,
	onUpdateField,
	isModalOpen = false,
}: StyleConfiguratorProps) {
	const colors = useThemeColors();
	const [showIconPicker, setShowIconPicker] = useState(false);
	const iconButtonRef = useRef<HTMLButtonElement>(null);
	// styleConfig is created alongside the field; default defensively so a
	// config that omits it can't crash the editor.
	const styleConfig: StyleConfiguration = field.styleConfig ?? { size: 'md' };
	// The icon picker persists the picked color as `iconColor`, which the
	// StyleConfiguration type doesn't (yet) declare — read through a wider
	// structural view.
	const iconStyleOptions:
		| { iconName?: string; iconColor?: string }
		| undefined = field.styleConfig;

	return (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center mb-3">
					<Icon
						icon={faRulerVertical}
						size="sm"
						className={`mr-2 ${colors.classes.textPrimary}`}
					/>
					<Text className="text-base font-semibold">Style Settings</Text>
				</div>

				{/* Size Selection */}
				<div className="mb-4">
					<Text className="text-sm font-medium mb-2">Size</Text>
					{/* `flex-wrap` keeps the 5 size buttons inside the configurator
              column on narrow modal widths — without it the row's
              min-content (~350px from "Extra Small/Large" labels) exceeds
              the column and the whole field configurator gets a horizontal
              scrollbar. `min-w-[80px]` keeps each button readable when it
              wraps (otherwise flex-1 + flex-basis 0 would let them shrink
              to icon-size). */}
					<div className="flex flex-wrap gap-2">
						{sizeOptions.map((size) => (
							<button
								key={size.value}
								onClick={() =>
									onUpdateField({
										styleConfig: { ...styleConfig, size: size.value },
									})
								}
								className={cn(
									'flex-1 min-w-[80px] py-2 px-3 text-xs rounded-md border-2 text-center transition-colors',
									field.styleConfig?.size === size.value
										? cn(
												colors.classes.bgInfo,
												colors.classes.borderPrimary,
												colors.classes.textPrimary,
												'font-semibold',
											)
										: cn(
												colors.classes.bgPrimary,
												colors.classes.borderDefault,
												colors.classes.textSecondary,
												'font-medium',
												colors.classes.bgPrimaryHover,
											),
								)}
							>
								{size.label}
							</button>
						))}
					</div>
				</div>

				{/* Icon Selection (for icon display style) */}
				{field.displayStyle === 'icon' && (
					<div className="mb-4">
						<Text className="text-sm font-medium mb-2">Icon</Text>
						<button
							ref={iconButtonRef}
							onClick={() => setShowIconPicker(true)}
							className={cn(
								'w-full flex items-center justify-between p-3 border-2 rounded-md',
								colors.classes.bgPrimary,
								colors.classes.borderDefault,
								colors.classes.bgPrimaryHover,
							)}
						>
							<div className="flex items-center">
								{field.styleConfig?.iconName ? (
									<>
										<Icon
											icon={
												getIconDefinition(field.styleConfig?.iconName) ||
												faIcons
											}
											size="sm"
											color={colors.values.textPrimary}
											className="mr-2"
										/>
										<Text className="text-sm">
											{field.styleConfig?.iconName}
										</Text>
									</>
								) : (
									<Text className="text-sm" color="secondary">
										Select an icon...
									</Text>
								)}
							</div>
							<Icon
								icon={faChevronDown}
								size="xs"
								className={colors.classes.textSecondary}
							/>
						</button>

						<IconDropdown
							visible={showIconPicker}
							onClose={() => setShowIconPicker(false)}
							onSelect={(iconName, iconColor) => {
								const nextStyleConfig: StyleConfiguration & {
									iconColor?: string;
								} = {
									...styleConfig,
									iconName,
									iconColor,
								};
								onUpdateField({ styleConfig: nextStyleConfig });
								setShowIconPicker(false);
							}}
							currentIcon={{
								name: iconStyleOptions?.iconName,
								color: iconStyleOptions?.iconColor,
							}}
							triggerRef={iconButtonRef as React.RefObject<HTMLElement>}
							isModalOpen={isModalOpen}
						/>
					</div>
				)}

				{/* Text Expansion Configuration (for text display style) */}
				{field.displayStyle === 'text' && (
					<div className="mb-4">
						<Text className="text-sm font-medium mb-2">Text Expansion</Text>

						{/* Enable Text Expansion Toggle */}
						<button
							onClick={() =>
								onUpdateField({
									styleConfig: {
										...styleConfig,
										textExpansion: {
											...field.styleConfig?.textExpansion,
											enabled: !field.styleConfig?.textExpansion?.enabled,
										},
									},
								})
							}
							className={cn(
								'w-full flex items-center p-2 rounded-md border transition-colors mb-2',
								field.styleConfig?.textExpansion?.enabled
									? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
									: cn(
											colors.classes.bgSecondary,
											colors.classes.borderDefault,
										),
							)}
						>
							<div
								className={cn(
									'w-4 h-4 rounded border-2 mr-2 flex items-center justify-center',
									field.styleConfig?.textExpansion?.enabled
										? cn(colors.classes.borderPrimary, colors.classes.bgPrimary)
										: colors.classes.borderDefault,
								)}
							>
								{field.styleConfig?.textExpansion?.enabled && (
									<div className="w-2 h-2 bg-white rounded-sm" />
								)}
							</div>
							<Text
								className={cn(
									'text-sm',
									field.styleConfig?.textExpansion?.enabled && 'font-medium',
								)}
							>
								Enable text expansion for long content
							</Text>
						</button>

						{/* Max Lines Configuration */}
						{field.styleConfig?.textExpansion?.enabled && (
							<div>
								<Text className="block text-xs font-medium mb-1" as="label">
									Maximum Lines
								</Text>
								<div className="flex gap-1 flex-wrap">
									{[1, 2, 3, 4, 5].map((lines) => (
										<button
											key={lines}
											onClick={() =>
												onUpdateField({
													styleConfig: {
														...styleConfig,
														textExpansion: {
															...field.styleConfig?.textExpansion,
															// This control only renders while expansion is
															// enabled (see the guard above).
															enabled:
																field.styleConfig?.textExpansion?.enabled ??
																true,
															maxLines: lines,
														},
													},
												})
											}
											className={cn(
												'py-1.5 px-3 text-xs rounded border transition-colors',
												(field.styleConfig?.textExpansion?.maxLines || 2) ===
													lines
													? cn(
															colors.classes.bgInfo,
															colors.classes.borderPrimary,
															colors.classes.textPrimary,
															'font-semibold',
														)
													: cn(
															colors.classes.bgPrimary,
															colors.classes.borderDefault,
															colors.classes.textSecondary,
															colors.classes.bgPrimaryHover,
														),
											)}
										>
											{lines}
										</button>
									))}
								</div>
								<Text className="text-xs mt-1" color="secondary">
									Number of lines to show before requiring expansion
								</Text>
							</div>
						)}
					</div>
				)}

				{/* Text Wrapping (for text display style) */}
				{field.displayStyle === 'text' && (
					<div className="mb-4">
						<div className="flex items-center mb-2">
							<Icon
								icon={faAlignJustify}
								size="xs"
								className={`mr-1.5 ${colors.classes.textPrimary}`}
							/>
							<Text className="text-sm font-medium">Text Wrapping</Text>
						</div>

						<button
							onClick={() =>
								onUpdateField({
									styleConfig: {
										...styleConfig,
										allowTextWrap: !field.styleConfig?.allowTextWrap,
									},
								})
							}
							className={cn(
								'w-full flex items-center p-2 rounded-md border transition-colors',
								field.styleConfig?.allowTextWrap
									? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
									: cn(
											colors.classes.bgSecondary,
											colors.classes.borderDefault,
										),
							)}
						>
							<div
								className={cn(
									'w-4 h-4 rounded border-2 mr-2 flex items-center justify-center',
									field.styleConfig?.allowTextWrap
										? cn(colors.classes.borderPrimary, colors.classes.bgPrimary)
										: colors.classes.borderDefault,
								)}
							>
								{field.styleConfig?.allowTextWrap && (
									<div className="w-2 h-2 bg-white rounded-sm" />
								)}
							</div>
							<Text
								className={cn(
									'text-sm',
									field.styleConfig?.allowTextWrap && 'font-medium',
								)}
							>
								Allow text to wrap within column
							</Text>
						</button>
						<Text className="text-xs mt-1" color="secondary">
							When disabled, text stays on single lines except for line breaks.
							When enabled, long text wraps to fit column width.
						</Text>
					</div>
				)}

				{/* Variant Selection (for applicable display styles) */}
				{['badge', 'pill', 'chip'].includes(field.displayStyle) && (
					<div className="mb-4">
						<Text className="text-sm font-medium mb-2">Variant</Text>
						<div className="flex gap-2">
							{variantOptions.map((variant) => (
								<button
									key={variant.value}
									onClick={() =>
										onUpdateField({
											styleConfig: {
												...styleConfig,
												variant: variant.value,
											},
										})
									}
									className={cn(
										'flex-1 py-2 px-3 text-xs rounded-md border-2 text-center transition-colors',
										field.styleConfig?.variant === variant.value
											? cn(
													colors.classes.bgInfo,
													colors.classes.borderPrimary,
													colors.classes.textPrimary,
													'font-semibold',
												)
											: cn(
													colors.classes.bgPrimary,
													colors.classes.borderDefault,
													colors.classes.textSecondary,
													'font-medium',
													colors.classes.bgPrimaryHover,
												),
									)}
								>
									{variant.label}
								</button>
							))}
						</div>
					</div>
				)}

				{/* Show Label (for dot display) */}
				{field.displayStyle === 'dot' && (
					<div className="mb-4">
						<button
							onClick={() =>
								onUpdateField({
									styleConfig: {
										...styleConfig,
										showLabel: !field.styleConfig?.showLabel,
									},
								})
							}
							className={cn(
								'w-full flex items-center p-2 rounded-md border transition-colors',
								field.styleConfig?.showLabel
									? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
									: cn(
											colors.classes.bgSecondary,
											colors.classes.borderDefault,
										),
							)}
						>
							<div
								className={cn(
									'w-4 h-4 rounded border-2 mr-2 flex items-center justify-center',
									field.styleConfig?.showLabel
										? cn(colors.classes.borderPrimary, colors.classes.bgPrimary)
										: colors.classes.borderDefault,
								)}
							>
								{field.styleConfig?.showLabel && (
									<div className="w-2 h-2 bg-white rounded-sm" />
								)}
							</div>
							<Text
								className={cn(
									'text-sm',
									field.styleConfig?.showLabel && 'font-medium',
								)}
							>
								Show label text next to dot
							</Text>
						</button>
						<Text className="text-xs mt-1" color="secondary">
							Display the field value as text next to the colored dot
						</Text>
					</div>
				)}

				{/* Image Configuration */}
				{field.displayStyle === 'image' && (
					<>
						{/* Image Size Selection */}
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Image Size</Text>
							<div className="flex gap-2 flex-wrap">
								{(
									[
										{ value: 'thumbnail', label: 'Thumbnail' },
										{ value: 'small', label: 'Small' },
										{ value: 'medium', label: 'Medium' },
										{ value: 'large', label: 'Large' },
										{ value: 'xlarge', label: 'X-Large' },
										{ value: 'responsive', label: 'Responsive' },
									] as const
								).map((size) => (
									<button
										key={size.value}
										onClick={() =>
											onUpdateField({
												styleConfig: {
													...styleConfig,
													imageOptions: {
														...field.styleConfig?.imageOptions,
														size: size.value,
													},
												},
											})
										}
										className={cn(
											'py-2 px-3 text-xs rounded-md border-2 text-center transition-colors',
											field.styleConfig?.imageOptions?.size === size.value
												? cn(
														colors.classes.bgInfo,
														colors.classes.borderPrimary,
														colors.classes.textPrimary,
														'font-semibold',
													)
												: cn(
														colors.classes.bgPrimary,
														colors.classes.borderDefault,
														colors.classes.textSecondary,
														'font-medium',
														colors.classes.bgPrimaryHover,
													),
										)}
									>
										{size.label}
									</button>
								))}
							</div>
						</div>

						{/* Enable Image Viewer */}
						<div className="mb-4">
							<button
								onClick={() =>
									onUpdateField({
										styleConfig: {
											...styleConfig,
											imageOptions: {
												...field.styleConfig?.imageOptions,
												enableViewer: !(
													field.styleConfig?.imageOptions?.enableViewer ?? true
												),
											},
										},
									})
								}
								className={cn(
									'w-full flex items-center p-2 rounded-md border transition-colors',
									(field.styleConfig?.imageOptions?.enableViewer ?? true)
										? cn(colors.classes.bgInfo, colors.classes.borderPrimary)
										: cn(
												colors.classes.bgSecondary,
												colors.classes.borderDefault,
											),
								)}
							>
								<div
									className={cn(
										'w-4 h-4 rounded border-2 mr-2 flex items-center justify-center',
										(field.styleConfig?.imageOptions?.enableViewer ?? true)
											? cn(
													colors.classes.borderPrimary,
													colors.classes.bgPrimary,
												)
											: colors.classes.borderDefault,
									)}
								>
									{(field.styleConfig?.imageOptions?.enableViewer ?? true) && (
										<div className="w-2 h-2 bg-white rounded-sm" />
									)}
								</div>
								<Text
									className={cn(
										'text-sm',
										(field.styleConfig?.imageOptions?.enableViewer ?? true) &&
											'font-medium',
									)}
								>
									Enable click to view full image
								</Text>
							</button>
							<Text className="text-xs mt-1" color="secondary">
								Allow users to click the image to open it in a full-size viewer
							</Text>
						</div>
					</>
				)}
			</div>
		</Card>
	);
}
